"use client";

import { CheckCircle2 } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { NewsletterSubscribeForm } from "./newsletter-subscribe-form";
import { useNewsletterSubscribe } from "./use-newsletter-subscribe";
import { useSubscriptionStatus } from "./use-subscription-status";

type Phase = "form" | "upsell" | "done";

export function SidebarNewsletter() {
  const {
    email,
    setEmail,
    subscribe,
    reportType,
    isSubmitting,
    isError,
    state,
    subscribeToEdition,
  } = useNewsletterSubscribe({ source: "sidebar" });

  const { subscribedToCurrent, subscribedToOther, otherEdition, otherLabel } =
    useSubscriptionStatus();

  const [upsellSubmitting, setUpsellSubmitting] = useState(false);
  const [upsellDone, setUpsellDone] = useState(false);

  const justSubscribed = state.status === "success";

  // Determine current phase
  let phase: Phase = "form";
  if (upsellDone) {
    phase = "done";
  } else if (justSubscribed && !subscribedToOther) {
    // Just subscribed this session — offer the other edition
    phase = "upsell";
  } else if (justSubscribed || subscribedToCurrent) {
    // Already subscribed (returning user or just finished) — hide
    return null;
  }

  const handleUpsell = async () => {
    setUpsellSubmitting(true);
    const ok = await subscribeToEdition({
      edition: otherEdition,
      includeEditions: ["engineer", "creative"],
    });
    setUpsellSubmitting(false);
    if (ok) {
      setUpsellDone(true);
    }
  };

  if (phase === "form") {
    return (
      <div className="mt-2 border-[var(--explore-border-light)] border-t p-0 pt-6">
        <NewsletterSubscribeForm
          alreadySubscribed={state.alreadySubscribed}
          currentEdition={reportType}
          email={email}
          errorMessage={state.message}
          isError={isError}
          isSubmitting={isSubmitting}
          isSuccess={false}
          onEmailChange={setEmail}
          onSubscribe={subscribe}
        />
      </div>
    );
  }

  return (
    <div
      className="mt-2 border-[var(--explore-border-light)] border-t p-0 pt-6 text-center"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <div className="mx-auto mb-3 h-16 w-16 overflow-hidden rounded-full">
        <Image
          alt="AI Primer mascot"
          className="h-full w-full object-cover"
          height={64}
          src="/logo.webp"
          width={64}
        />
      </div>

      {/* ── Upsell other edition ── */}
      {phase === "upsell" && (
        <>
          <div className="mb-3 flex items-center justify-center gap-1.5">
            <CheckCircle2 className="h-5 w-5 text-[var(--explore-success-text)]" />
            <span className="font-semibold text-[0.85rem] text-[var(--explore-success-text)]">
              Subscribed!
            </span>
          </div>
          <p className="mb-3 text-[0.75rem] text-[var(--explore-text-muted)]">
            Want to also get the {otherLabel} digest?
          </p>
          <Button
            className="w-full rounded-full border-none bg-[var(--explore-accent-yellow)] font-semibold text-[0.8rem] text-[var(--explore-on-accent-text)] shadow-none transition-transform duration-150 hover:bg-[var(--explore-accent-yellow)]/90 active:scale-95"
            onClick={handleUpsell}
            type="button"
          >
            {upsellSubmitting ? "Adding..." : `Yes, add ${otherLabel} too`}
          </Button>
        </>
      )}

      {/* ── All done (briefly visible after upsell) ── */}
      {phase === "done" && upsellDone && (
        <div className="flex flex-col items-center gap-1.5 py-1">
          <CheckCircle2 className="h-6 w-6 text-[var(--explore-success-text)]" />
          <span className="font-semibold text-[0.85rem] text-[var(--explore-success-text)]">
            You're getting both digests!
          </span>
        </div>
      )}
    </div>
  );
}
